<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AiDraftBubble from './AiDraftBubble.vue';
import DeskAvatar from './DeskAvatar.vue';
import DeskBubbleTail from './DeskBubbleTail.vue';

const props = defineProps({
    messages: { type: Array, default: () => [] },
    labels: { type: Object, default: () => ({}) },
    aiDraft: { type: Object, default: null },
    aiDraftBody: { type: String, default: '' },
    composer: { type: Object, default: () => ({}) },
    canAddMessage: { type: Boolean, default: false },
    assistBusy: { type: Boolean, default: false },
    maxAttachmentMb: { type: Number, default: 25 },
});

const emit = defineEmits([
    'update:aiDraftBody', 'send-ai-draft', 'discard-draft', 'clear-draft-ui', 'toast-error',
]);

const rootEl = ref(null);
const lightbox = ref(null);

const showAiDraftBubble = computed(() => Boolean(props.aiDraft?.visible));

const isInbound = (message) => message.inbound !== false && message.direction !== 'outbound';

const messageRole = (message) => {
    if (message.actor_role === 'customer' || message.actor_role === 'operator' || message.actor_role === 'ai') {
        return message.actor_role;
    }
    return isInbound(message) ? 'customer' : 'operator';
};

const displayLabel = (message) => message.sender_name || message.sender || message.label || '';

const messageAttachments = (message) => {
    const rows = message.attachments;
    return Array.isArray(rows) ? rows : [];
};

const isPreviewableAttachment = (attachment) => attachment.previewable === true
    && attachment.available !== false
    && Boolean(attachment.downloadUrl);

const openLightbox = (attachment) => {
    if (!isPreviewableAttachment(attachment)) {
        return;
    }
    lightbox.value = {
        downloadUrl: attachment.downloadUrl,
        filename: attachment.filename || '',
    };
};

const closeLightbox = () => {
    lightbox.value = null;
};

const onLightboxKeydown = (event) => {
    if (event.key === 'Escape') {
        closeLightbox();
    }
};

const scrollToBottom = async () => {
    await nextTick();
    const el = rootEl.value;
    if (el) {
        el.scrollTop = el.scrollHeight;
    }
};

onMounted(() => {
    scrollToBottom();
    window.addEventListener('keydown', onLightboxKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onLightboxKeydown);
});

watch(() => props.messages.length, scrollToBottom);
watch(() => props.aiDraft?.visible, (visible) => {
    if (visible) {
        scrollToBottom();
    }
});
</script>

<template>
    <section ref="rootEl" class="ticket-desk-thread" data-testid="ticket-desk-thread">
        <div class="ticket-desk-thread__inner">
            <TransitionGroup
                v-if="messages.length"
                name="ticket-desk-msg"
                tag="div"
                class="ticket-desk-thread__list"
            >
                <div
                    v-for="(message, index) in messages"
                    :key="message.id || `msg-${index}`"
                    class="ticket-desk-msg-wrap"
                >
                    <div v-if="message.date_separator" class="ticket-desk-thread__separator">
                        <span>{{ message.date_separator }}</span>
                    </div>
                    <article
                        class="ticket-desk-message"
                        :class="{ 'ticket-desk-message--outbound': !isInbound(message) }"
                    >
                        <DeskAvatar
                            :role="messageRole(message)"
                            :label="displayLabel(message)"
                            :avatar-url="message.avatar_url || message.avatarUrl || ''"
                            size="thread"
                        />
                        <div class="ticket-desk-message__bubble">
                            <DeskBubbleTail :side="isInbound(message) ? 'in' : 'out'" />
                            <div class="ticket-desk-message__meta">
                                <strong>{{ displayLabel(message) }}</strong>
                                <time>{{ message.time || message.created_at_label }}</time>
                            </div>
                            <p>{{ message.text || message.body }}</p>
                            <ul
                                v-if="messageAttachments(message).length"
                                class="ticket-desk-message__attachments"
                                data-testid="ticket-desk-message-attachments"
                            >
                                <li
                                    v-for="(attachment, attachIndex) in messageAttachments(message)"
                                    :key="attachment.downloadUrl || attachment.filename || attachIndex"
                                    :class="{ 'ticket-desk-message__attach--preview': isPreviewableAttachment(attachment) }"
                                >
                                    <template v-if="isPreviewableAttachment(attachment)">
                                        <button
                                            type="button"
                                            class="ticket-desk-message__thumb-btn"
                                            data-testid="ticket-desk-attachment-thumb"
                                            :aria-label="labels.enlargeImage || 'Ingrandisci immagine'"
                                            @click="openLightbox(attachment)"
                                        >
                                            <img
                                                class="ticket-desk-message__thumb"
                                                :src="attachment.downloadUrl"
                                                :alt="attachment.filename"
                                                loading="lazy"
                                            >
                                        </button>
                                        <span class="ticket-desk-message__attach-name">{{ attachment.filename }}</span>
                                    </template>
                                    <template v-else>
                                        <span aria-hidden="true">📎</span>
                                        <a
                                            v-if="attachment.available !== false && attachment.downloadUrl"
                                            :href="attachment.downloadUrl"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {{ attachment.filename }}
                                        </a>
                                        <span v-else>{{ attachment.filename }}</span>
                                    </template>
                                    <span
                                        v-if="attachment.formattedSize"
                                        class="ticket-desk-message__attach-size"
                                    >
                                        ({{ attachment.formattedSize }})
                                    </span>
                                    <span
                                        v-else-if="attachment.available === false"
                                        class="ticket-desk-message__attach-size"
                                    >
                                        ({{ labels.fileUnavailable || 'non disponibile' }})
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </article>
                </div>
            </TransitionGroup>
            <p v-else-if="!showAiDraftBubble" class="ticket-desk-empty">{{ labels.noMessages || 'Nessun messaggio' }}</p>

            <Transition name="ticket-desk-ai-draft">
                <div
                    v-if="showAiDraftBubble"
                    key="ai-draft"
                    class="ticket-desk-msg-wrap ticket-desk-msg-wrap--ai-draft"
                >
                    <AiDraftBubble
                        :body="aiDraftBody"
                        :composer="composer"
                        :labels="labels"
                        :can-add-message="canAddMessage"
                        :assist-busy="assistBusy"
                        :max-attachment-mb="maxAttachmentMb"
                        @update:body="emit('update:aiDraftBody', $event)"
                        @send-ai-draft="emit('send-ai-draft', $event)"
                        @discard-draft="emit('discard-draft')"
                        @clear-draft-ui="emit('clear-draft-ui')"
                        @toast-error="emit('toast-error', $event)"
                    />
                </div>
            </Transition>
        </div>

        <div
            v-if="lightbox"
            class="ticket-desk-attach-lightbox"
            data-testid="ticket-desk-attachment-lightbox"
            role="dialog"
            aria-modal="true"
            :aria-label="lightbox.filename || labels.enlargeImage || 'Anteprima allegato'"
            @click.self="closeLightbox"
        >
            <div class="ticket-desk-attach-lightbox__panel">
                <button
                    type="button"
                    class="ticket-desk-attach-lightbox__close"
                    data-testid="ticket-desk-attachment-lightbox-close"
                    @click="closeLightbox"
                >
                    {{ labels.close || 'Chiudi' }}
                </button>
                <img
                    class="ticket-desk-attach-lightbox__img"
                    :src="lightbox.downloadUrl"
                    :alt="lightbox.filename"
                >
                <a
                    class="ticket-desk-attach-lightbox__download"
                    data-testid="ticket-desk-attachment-lightbox-download"
                    :href="lightbox.downloadUrl"
                    :download="lightbox.filename || true"
                >
                    {{ labels.downloadAttachment || 'Scarica' }}
                </a>
            </div>
        </div>
    </section>
</template>
